import { API_CONFIG } from '../utils/apiConfig';
import { createApiClient } from '../utils/apiClient';

const DEFAULT_BASE_URL = API_CONFIG.QUESTIONNAIRE_API_URL;

export interface QuestionnaireServiceConfig {
    baseUrl?: string;
    /**
     * Optional access token override.
     * If not provided, the token will be automatically retrieved from session storage.
     */
    accessToken?: string;
}

export type QuestionnairePayload = Record<string, unknown>;

/**
 * Gets the API client with automatic token handling.
 * If accessToken is provided in config, it will be used.
 * Otherwise, the token is automatically retrieved from session storage.
 */
function getApiClient(config?: QuestionnaireServiceConfig) {
    const baseUrl = config?.baseUrl || DEFAULT_BASE_URL;

    if (!baseUrl) {
        throw new Error('Missing API base URL for Questionnaire service');
    }

    return createApiClient({
        baseUrl,
        accessToken: config?.accessToken,
    });
}

type UseContextEntry = {
    code?: { system?: string; code?: string };
    valueCodeableConcept?: { coding?: Array<{ code?: string }>; text?: string };
};

export const QUESTIONNAIRE_USE_CONTEXT: UseContextEntry = {
    code: {
        system: 'http://terminology.hl7.org/CodeSystem/usage-context-type',
        code: 'workflow',
    },
    valueCodeableConcept: {
        coding: [{ code: 'questionnaire' }],
        text: 'questionnaire',
    },
};

export const THERAPY_MATERIAL_USE_CONTEXT: UseContextEntry = {
    code: {
        system: 'http://terminology.hl7.org/CodeSystem/usage-context-type',
        code: 'workflow',
    },
    valueCodeableConcept: {
        coding: [{ code: 'therapy-material' }],
        text: 'therapy-material',
    },
};

const withWorkflowUseContext = (
    payload: QuestionnairePayload,
    workflowUseContext: UseContextEntry,
): QuestionnairePayload => {
    const existing = Array.isArray((payload as { useContext?: unknown }).useContext)
        ? ([...(payload as { useContext: unknown[] }).useContext] as UseContextEntry[])
        : [];

    const otherUseContexts = existing.filter(
        (entry) =>
            entry?.code?.system !== workflowUseContext.code?.system ||
            entry.code?.code !== workflowUseContext.code?.code,
    );

    return {
        ...payload,
        useContext: [...otherUseContexts, workflowUseContext],
    };
};

/**
 * Ensures that the normal questionnaire editor writes exactly one workflow kind.
 * Other use contexts are preserved, while conflicting questionnaire-kind entries
 * are removed. Avoids mutating the original object.
 */
export const withQuestionnaireUseContext = (payload: QuestionnairePayload): QuestionnairePayload => {
    return withWorkflowUseContext(payload, QUESTIONNAIRE_USE_CONTEXT);
};

export const withTherapyMaterialUseContext = (payload: QuestionnairePayload): QuestionnairePayload =>
    withWorkflowUseContext(payload, THERAPY_MATERIAL_USE_CONTEXT);

/**
 * Questionnaires and therapy material are shared with every therapist unless they carry this
 * meta tag. The backend only honours it on creation; visibility cannot be changed afterwards.
 */
export const VISIBILITY_TAG_SYSTEM = 'http://quala.health/fhir/CodeSystem/questionnaire-visibility';
export const PRIVATE_VISIBILITY_CODE = 'private';

type MetaTag = { system?: string; code?: string };
type ResourceMeta = { tag?: MetaTag[]; versionId?: string; [key: string]: unknown };
type ResourceWithMeta = { meta?: ResourceMeta };

export const isPrivateResource = (resource: { meta?: { tag?: MetaTag[] } } | undefined): boolean =>
    Boolean(
        resource?.meta?.tag?.some(
            (tag) => tag?.system === VISIBILITY_TAG_SYSTEM && tag.code === PRIVATE_VISIBILITY_CODE,
        ),
    );

/**
 * Sets exactly one private tag, or none for a shared resource. Other tags (e.g. language) are
 * preserved. Avoids mutating the original object.
 */
export const withVisibility = (payload: QuestionnairePayload, isPrivate: boolean): QuestionnairePayload => {
    const meta: ResourceMeta = (payload as ResourceWithMeta).meta ?? {};
    const otherTags = (Array.isArray(meta.tag) ? meta.tag : []).filter((tag) => tag?.system !== VISIBILITY_TAG_SYSTEM);

    return {
        ...payload,
        meta: {
            ...meta,
            tag: isPrivate
                ? [...otherTags, { system: VISIBILITY_TAG_SYSTEM, code: PRIVATE_VISIBILITY_CODE }]
                : otherTags,
        },
    };
};

export interface CreateQuestionnaireOptions {
    /** Only visible to the creating therapist. Defaults to shared. */
    isPrivate?: boolean;
}

export async function createQuestionnaire<T = unknown>(
    payload: QuestionnairePayload,
    config?: QuestionnaireServiceConfig,
    options?: CreateQuestionnaireOptions,
): Promise<T | undefined> {
    if (!payload) {
        throw new Error('Questionnaire payload is required');
    }

    const client = getApiClient(config);
    // Guarantee the Questionnaire is tagged with the standard useContext before sending.
    const payloadWithUseContext = withQuestionnaireUseContext(payload);
    return client.post<T>('/Questionnaire', withVisibility(payloadWithUseContext, Boolean(options?.isPrivate)));
}

export async function createTherapyMaterial<T = unknown>(
    payload: QuestionnairePayload,
    config?: QuestionnaireServiceConfig,
    options?: CreateQuestionnaireOptions,
): Promise<T | undefined> {
    if (!payload) {
        throw new Error('Therapy material payload is required');
    }

    const client = getApiClient(config);
    return client.post<T>(
        '/Questionnaire',
        withVisibility(withTherapyMaterialUseContext(payload), Boolean(options?.isPrivate)),
    );
}

export type QuestionnaireUpdateResult<T> = {
    resource: T | undefined;
    /**
     * Patients had already answered the changed content, so the backend saved the edit as a new
     * questionnaire and archived the old one. Existing assignments keep the old version.
     */
    createdNewVersion: boolean;
};

async function putQuestionnaire<T extends { id?: string }>(
    id: string,
    payload: QuestionnairePayload,
    versionId: string,
    config?: QuestionnaireServiceConfig,
): Promise<QuestionnaireUpdateResult<T>> {
    if (!id) {
        throw new Error('Questionnaire id is required');
    }
    if (!versionId) {
        throw new Error('Questionnaire version is required');
    }

    const meta: ResourceMeta = (payload as ResourceWithMeta).meta ?? {};
    const client = getApiClient(config);
    // The version travels in If-Match and, in case a proxy drops the header, in meta.versionId.
    const resource = await client.put<T>(
        `/Questionnaire/${id}`,
        { ...payload, id, meta: { ...meta, versionId } },
        { headers: { 'If-Match': `W/"${versionId}"` } },
    );

    return {
        resource,
        createdNewVersion: Boolean(resource?.id && resource.id !== id),
    };
}

/**
 * Saves an edit of a shared questionnaire for every therapist. Rejected with status 412 when
 * someone else saved a newer version since `versionId` was loaded.
 */
export async function updateQuestionnaire<T extends { id?: string } = { id?: string }>(
    id: string,
    payload: QuestionnairePayload,
    versionId: string,
    config?: QuestionnaireServiceConfig,
): Promise<QuestionnaireUpdateResult<T>> {
    return putQuestionnaire<T>(id, withQuestionnaireUseContext(payload), versionId, config);
}

export async function updateTherapyMaterial<T extends { id?: string } = { id?: string }>(
    id: string,
    payload: QuestionnairePayload,
    versionId: string,
    config?: QuestionnaireServiceConfig,
): Promise<QuestionnaireUpdateResult<T>> {
    return putQuestionnaire<T>(id, withTherapyMaterialUseContext(payload), versionId, config);
}

export async function deleteQuestionnaire<T = unknown>(
    id: string,
    config?: QuestionnaireServiceConfig,
): Promise<T | undefined> {
    if (!id) {
        throw new Error('Questionnaire id is required');
    }

    const client = getApiClient(config);
    return client.delete<T>(`/Questionnaire/${id}`);
}
